import { Children, ReactElement, ReactNode, createElement, isValidElement } from "react";

import { Break } from "../Break";
import { Span, SpanProps } from "../Span";
import { Spread, SpreadProps } from "../Spread";

/**
 * A cell to be placed within the grid.
 */
export interface Cell {
  /**
   * The number of columns spanned by the cell.
   */
  readonly span: number;

  /**
   * The React child element associated with the cell.
   */
  readonly child: ReactNode;

  /**
   * A stable key for React reconciliation. Uses user-provided keys when available,
   * falls back to index-based keys.
   */
  readonly key: string;
}

/**
 * Extracts a stable key from a React child element.
 * Prefers user-provided keys, falls back to index-based keys.
 * @param child The React child to extract a key from.
 * @param index The index of the child in the children array.
 * @returns A stable key string.
 */
function getChildKey(child: ReactNode, index: number): string {
  if (isValidElement(child) && child.key != null) {
    const key = String(child.key);
    // Children.toArray() adds ".$" or ".:" prefixes - strip them to get the original key
    if (key.startsWith(".$") || key.startsWith(".:")) {
      return `child-${key.slice(2)}`;
    }
    // If it starts with a dot followed by any character, it's likely a React-prefixed key
    if (key.startsWith(".")) {
      return `child-${key.slice(1)}`;
    }
    return `child-${key}`;
  }
  return `idx-${index}`;
}

/**
 * Represents a child with its associated key for row processing.
 */
interface KeyedChild {
  readonly child: ReactNode;
  readonly key: string;
}

/**
 * Splits the specified set of React children into discrete rows that would fit into a grid with the specified number of columns.
 * @param children The React children to split rows from.
 * @param columnCount The number of columns in the grid.
 * @returns The split rows.
 */
function splitRows(children: ReactNode, columnCount: number): ReadonlyArray<ReadonlyArray<KeyedChild>> {
  const rows: Array<ReadonlyArray<KeyedChild>> = [];
  let counter = 0;
  let currentRow: Array<KeyedChild> = [];

  function handleElement(child: ReactNode, key: string): void {
    if (!child) return;
    currentRow.push({ child, key });
    counter++;
  }

  function handleBreak(): void {
    counter = columnCount;
  }

  function handleSpan(child: ReactElement<SpanProps>, key: string): void {
    let { columns } = child.props;
    if (columns <= 0) return;

    if (columns > columnCount) columns = columnCount;

    if (counter + columns > columnCount) {
      rows.push(currentRow);
      currentRow = [{ child, key }];
      counter = columns;
    } else {
      currentRow.push({ child, key });
      counter += columns;
    }
  }

  function handleSpread(child: ReactElement<SpreadProps>, parentKey: string): void {
    let { columns } = child.props;
    const { children } = child.props;
    if (columns <= 0) return;

    if (columns > columnCount) columns = columnCount;

    if (!children) return;

    for (const [index, spanChild] of Children.toArray(children).entries()) {
      if (!spanChild) continue;
      const childKey = getChildKey(spanChild, index);
      // Use the inner child's key for stability
      handleSpan(createElement(Span, { columns }, spanChild), `${parentKey}.${childKey}`);
    }
  }

  for (const [index, child] of Children.toArray(children).entries()) {
    const key = getChildKey(child, index);
    if (!isValidElement(child) || (child.type !== Span && child.type !== Break && child.type !== Spread)) {
      handleElement(child, key);
    } else if (child.type === Break && counter > 0) {
      handleBreak();
    } else if (child.type === Spread) {
      handleSpread(child as ReactElement<SpreadProps>, key);
    } else if (child.type === Span) {
      handleSpan(child as ReactElement<SpanProps>, key);
    }

    if (counter === columnCount) {
      counter = 0;
      if (currentRow.length > 0) {
        rows.push(currentRow);
        currentRow = [];
      }
    }
  }

  if (currentRow.length > 0) {
    rows.push(currentRow);
  }

  return rows;
}

/**
 * Gets the individual cell spans that make up the grid.
 * @param children The children to get cells from.
 * @param columnCount The number of columns in the grid.
 * @returns The split cells.
 */
export function getCells(children: ReactNode, columnCount: number): ReadonlyArray<Cell> {
  const rows = splitRows(children, columnCount);
  const cells: Array<Cell> = [];

  for (const [rowIndex, row] of rows.entries()) {
    const spans = row.filter(({ child }) => isValidElement(child) && child.type === Span) as ReadonlyArray<{
      child: ReactElement<SpanProps>;
      key: string;
    }>;
    const reservedColumns = spans.reduce<number>((previousValue, { child }) => previousValue + (child.props as SpanProps).columns, 0);
    const availableColumns = columnCount - reservedColumns;
    const nonSpanCount = row.length - spans.length;
    const columnsPerChild = nonSpanCount > 0 ? Math.floor(availableColumns / nonSpanCount) : 0;

    // A row consisting of only spans with no children can be stripped out.
    if (spans.length === row.length) {
      if (!spans.some(({ child: span }) => span.props.children)) {
        continue;
      }
    }

    let count = 0;

    for (const { child, key } of row) {
      if (isValidElement(child) && child.type === Span) {
        const { columns, children } = child.props as SpanProps;
        cells.push({ span: columns, child: children, key });
        count += columns;
      } else {
        cells.push({ span: columnsPerChild, child, key });
        count += columnsPerChild;
      }
    }

    const padSize = columnCount - count;
    if (padSize > 0) {
      cells.push({ span: padSize, child: null, key: `pad-${rowIndex}` });
    }
  }

  return cells;
}
